import { Component, EventEmitter, Input, model, OnDestroy, OnInit, Output, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Pet } from '@app/models/pet.model';
import { UserAccount } from '@app/models/userAccount.model';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, ONLINE_ID, PET_LIST } from '@app/services/local-storage.service';
import { NavigationService } from '@app/services/navigation.service';
import { PetService } from '@app/services/pet.service';
import { UserAccountService } from '@app/services/user-account.service';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { IBookingStepComponent } from '../IBookingStepComponent';

@Component({
    selector: 'app-pet-list',
    templateUrl: './pet-list.component.html',
    styleUrls: ['./pet-list.component.css'],
    standalone: false,
})
export class PetListComponent implements OnInit, OnDestroy, IBookingStepComponent {
    pets: Pet[] = [];
    selectedPetName: string = '';
    public formGroup: FormGroup;
    private ngUnsubscribe: Subject<any> = new Subject();
    private navigationButtonSubscription!: Subscription;
    @Input() disabled: boolean = false;
    @Output() isValidFormChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() addPetClick: EventEmitter<void> = new EventEmitter<void>();
    @Output() logoutClick: EventEmitter<void> = new EventEmitter<void>();
    public readonly sheetRef = viewChild<BrnSheet>('sheet');
    public currentUser: UserAccount | undefined;
    public displayName: string = '';

    public refreshPetList(): void {
        this.fetchPetList();
    }

    // Implementation of IBookingStepComponent
    public continueButtonText: string = ButtonName.ReviewDetails;
    public nextStepRoute: string = 'review';
    public previousStepRoute: string = 'client';
    public titleBarTitle = 'Select your pet';
    public showTitleBarAccountSection = true;
    public titleBarHeight = model<string>();
    public isValid(): boolean {
        return this.formGroup.valid;
    }

    constructor(
        private localStorageService: LocalStorageService,
        private petService: PetService,
        private navigationService: NavigationService,
        private fb: FormBuilder,
        private userAccountService: UserAccountService
    ) {
        this.formGroup = this.fb.group({
            petList: new FormControl('', Validators.required),
        });
    }

    ngOnInit(): void {
        this.currentUser = this.userAccountService.getCurrentUser();
        this.updateDisplayName();
        this.userAccountService.userAccountUpdated$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((userAccount) => {
            this.currentUser = userAccount ?? undefined;
            this.updateDisplayName();
        });

        this.fetchPetList();

        this.navigationButtonSubscription = this.navigationService
            .getNavigationButtonName()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((buttonName) => {
                if (buttonName === ButtonName.ReviewDetails) {
                    const selectedPet = this.pets?.find((pet) => pet.name === this.formGroup.value.petList);
                    this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.PetInfo, selectedPet);
                }
            });

        this.formGroup.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            const isValid = status === 'VALID';
            this.navigationService.setIsStepFormValid(isValid);
            this.isValidFormChange.emit(isValid);
        });

        this.previouslySelectedPetInfo();
    }

    private previouslySelectedPetInfo(): void {
        const petList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        const appointmentPetInfo = appointmentRequest?.petInfo || null;

        if (appointmentPetInfo) {
            const isPetMatching = petList?.some(
                (pet) =>
                    pet.name === appointmentPetInfo.name &&
                    pet.petType?.name === appointmentPetInfo.petType?.name &&
                    pet.petBreed?.name === appointmentPetInfo.petBreed?.name &&
                    pet.weight === appointmentPetInfo.weight
            );

            if (isPetMatching) {
                this.formGroup.patchValue({ petList: appointmentPetInfo.name });
            } else {
                this.formGroup.patchValue({ petList: null });
            }
            this.navigationService.setIsStepFormValid(isPetMatching);
            this.isValidFormChange.emit(isPetMatching);
        } else {
            this.navigationService.setIsStepFormValid(false);
            this.isValidFormChange.emit(false);
        }
    }

    private fetchPetList(): void {
        const customerID = this.localStorageService.getString(CUSTOMER_ID);
        const onlineID = this.localStorageService.getString(ONLINE_ID);

        this.petService
            .getPetList(customerID, onlineID)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((pets) => {
                const apiPets = (pets || []).filter((pet) => pet && pet.name);
                const localPets = ((this.localStorageService.get(PET_LIST) as Pet[]) || []).filter((pet) => pet && pet.name);
                const mergedPets = [...apiPets];
                for (const localPet of localPets) {
                    const exists = mergedPets.some(
                        (p) =>
                            p.name === localPet.name &&
                            p.petType?.name === localPet.petType?.name &&
                            p.petBreed?.name === localPet.petBreed?.name &&
                            p.weight === localPet.weight
                    );
                    if (!exists) {
                        mergedPets.push(localPet);
                    }
                }
                this.pets = mergedPets;
                const previouslySelectedPetName = this.formGroup.value.petList;

                this.selectedPetName = this.pets.some((pet) => pet.name === previouslySelectedPetName) ? previouslySelectedPetName : '';

                this.formGroup.patchValue({ petList: this.selectedPetName });
            });
    }

    public addPet(): void {
        this.addPetClick.emit();
    }

    public onLogout(): void {
        this.userAccountService.logout();
        this.logoutClick.emit();
    }

    private updateDisplayName(): void {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const client = appointmentRequest?.client;

        const fullName = [client?.firstName, client?.lastName].filter(Boolean).join(' ').trim();
        this.displayName = fullName || client?.email || '';
    }

    public selectPet(petName: string): void {
        this.formGroup.patchValue({ petList: petName });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
        this.navigationButtonSubscription.unsubscribe();
    }
}
